import { useEffect, useState } from "react";
import { Container, Grid } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import { IsMobile900 } from "utils/is-mobile";

import GlobalPeriodFilter from "./components/GlobalPeriodFilter";
import SummaryCards from "./components/SummaryCards";
import TableEncontroContas from "./components/TableEncontroContas";
import { mapPedidosFromApi, mapSummaryFromApi } from "./components/utils";

const DEFAULT_START_DATE = moment().subtract(30, "days").format("YYYY-MM-DD");
const DEFAULT_END_DATE = moment().format("YYYY-MM-DD");

function formatDateForApi(date) {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  const momentDate = moment(date, ["MM-DD-YYYY", "DD-MM-YYYY", "YYYY-MM-DD"]);
  if (momentDate.isValid()) {
    return momentDate.format("YYYY-MM-DD");
  }
  return date;
}

export default function EncontroContas() {
  const snackbar = useSnackbar();
  const isMobile900 = IsMobile900();

  const [globalStartDate, setGlobalStartDate] = useState(DEFAULT_START_DATE);
  const [globalEndDate, setGlobalEndDate] = useState(DEFAULT_END_DATE);
  const [appliedStartDate, setAppliedStartDate] = useState(DEFAULT_START_DATE);
  const [appliedEndDate, setAppliedEndDate] = useState(DEFAULT_END_DATE);

  const [summaryData, setSummaryData] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const fetchSummary = async (startDate, endDate) => {
    const dtIni = formatDateForApi(startDate);
    const dtFim = formatDateForApi(endDate);
    const response = await Api.get(
      "encontro-contas-resumo",
      "reportApp",
      `?dtIni=${dtIni}&dtFim=${dtFim}`,
      true,
    );

    return mapSummaryFromApi(response?.Result ?? response);
  };

  const fetchPedidos = async (startDate, endDate, page, pageSize) => {
    const dtIni = formatDateForApi(startDate);
    const dtFim = formatDateForApi(endDate);
    const response = await Api.get(
      "encontro-contas-pedidos",
      "reportApp",
      `?dtIni=${dtIni}&dtFim=${dtFim}&page=${page}&pageSize=${pageSize}`,
      true,
    );

    return mapPedidosFromApi(response?.Result ?? response);
  };

  const loadSummary = async (startDate, endDate) => {
    setIsSummaryLoading(true);

    try {
      const summary = await fetchSummary(startDate, endDate);
      setSummaryData(summary);
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Erro ao carregar resumo do encontro de contas.",
        { severity: "error" },
      );
      setSummaryData(null);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const loadPedidos = async (
    startDate,
    endDate,
    page = currentPage,
    pageSize = currentSizePerPage,
  ) => {
    setIsTableLoading(true);

    try {
      const pedidosResult = await fetchPedidos(startDate, endDate, page, pageSize);

      setPedidos(pedidosResult.pedidos);
      setTotalRegistros(pedidosResult.totalRegistros);
      setCurrentPage(page);
      setCurrentSizePerPage(pageSize);
      setHasSearched(true);
    } catch (error) {
      snackbar.showMessage(
        error?.message || "Erro ao carregar pedidos do encontro de contas.",
        { severity: "error" },
      );
      setPedidos([]);
      setTotalRegistros(0);
      setHasSearched(true);
    } finally {
      setIsTableLoading(false);
    }
  };

  const loadWithFilter = (startDate, endDate, page = 0, pageSize = currentSizePerPage) => {
    loadSummary(startDate, endDate);
    loadPedidos(startDate, endDate, page, pageSize);
  };

  const handleApplyGlobalFilter = () => {
    setAppliedStartDate(globalStartDate);
    setAppliedEndDate(globalEndDate);
    loadWithFilter(globalStartDate, globalEndDate, 0, currentSizePerPage);
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      loadPedidos(
        appliedStartDate,
        appliedEndDate,
        page - 1,
        sizePerPage,
      );
    }
  };

  useEffect(() => {
    loadWithFilter(DEFAULT_START_DATE, DEFAULT_END_DATE, 0, currentSizePerPage);
  }, []);

  return (
    <>
      <HeaderClientes section="Encontro de Contas" subsection="Clientes" />
      <Container
        maxWidth={false}
        style={{
          backgroundColor: "#f1f5f9",
          paddingBottom: "2rem",
        }}
      >
        <GlobalPeriodFilter
          startDate={globalStartDate}
          endDate={globalEndDate}
          onStartDateChange={setGlobalStartDate}
          onEndDateChange={setGlobalEndDate}
          onApply={handleApplyGlobalFilter}
        />

        {isSummaryLoading ? (
          <Grid container spacing={3} style={{ marginBottom: "1.5rem" }}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={isMobile900 ? 12 : 6} md={3} key={item}>
                <Skeleton
                  variant="rect"
                  height={120}
                  style={{ borderRadius: 4 }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <SummaryCards data={summaryData} isMobile={isMobile900} />
        )}

        <TableEncontroContas
          data={pedidos}
          totalRegistros={totalRegistros}
          page={currentPage}
          sizePerPage={currentSizePerPage}
          onTableChange={handleTableChange}
          searched={hasSearched}
          loading={isTableLoading}
        />
      </Container>
    </>
  );
}
